import { useMemo } from 'react';
import type { MovimentacaoItemIntake, MovimentacaoEquipamentoView } from '../../../models';
import { useEstadiasProntasNoClienteQuery, useEquipamentosQuery, useMovimentacaoMutations } from '../../../hooks/data';
import { normalizarPatrimonio } from '../../../models/equipamento.utils';

interface SugestaoEstadiaClienteSectionProps {
  solicitacaoId: string;
  movimentacaoId: string;
  equipamentosIntake: MovimentacaoItemIntake[];
  item: MovimentacaoEquipamentoView;
  clienteId: string | null;
  colaboradorId: string | null;
}

/**
 * Fase 2 do fluxo de máquina homologada por Cliente — sugere equipamentos já prontos
 * (EstadiaCliente com status 'pronta_aguardando_destino') no Cliente desta Movimentação de
 * envio, pra o SI escolher em vez de vincular manualmente do zero (ver
 * VincularEquipamentoSection, que continua funcionando normalmente se nenhuma sugestão for
 * usada). Escolher uma sugestão aqui marca `equipamentoSugeridoId`/`estadiaClienteOrigemId`
 * no item — a Cloud Function onMovimentacaoWriteAplicarEquipamentoSugerido faz o resto
 * (preenche equipamentosExecucao e encerra a estadia).
 *
 * Decisão de produto: essa escolha fica só aqui (execução, exclusiva do SI), nunca na
 * abertura da Solicitação — em Admissão o colaborador muitas vezes ainda nem tem id real
 * no Radar nesse momento (é só ColaboradorPendente).
 */
export default function SugestaoEstadiaClienteSection({
  solicitacaoId,
  movimentacaoId,
  equipamentosIntake,
  item,
  clienteId,
  colaboradorId,
}: SugestaoEstadiaClienteSectionProps) {
  const { data: estadiasProntas = [] } = useEstadiasProntasNoClienteQuery(clienteId ?? undefined);
  const { data: equipamentos = [] } = useEquipamentosQuery();
  const { atualizarMovimentacao } = useMovimentacaoMutations(solicitacaoId);

  const equipamentoPorId = useMemo(() => new Map(equipamentos.map((equipamento) => [equipamento.id, equipamento])), [
    equipamentos,
  ]);

  const estadiasOrdenadas = useMemo(
    () =>
      [...estadiasProntas].sort((a, b) => {
        const aCotada = a.colaboradorCotadoId === colaboradorId && colaboradorId != null;
        const bCotada = b.colaboradorCotadoId === colaboradorId && colaboradorId != null;
        return aCotada === bCotada ? 0 : aCotada ? -1 : 1;
      }),
    [estadiasProntas, colaboradorId]
  );

  if (estadiasOrdenadas.length === 0) return null;

  const handleEscolher = (estadiaId: string, equipamentoId: string) => {
    const itensAtualizados = equipamentosIntake.map((intakeItem) =>
      intakeItem.id === item.id
        ? { ...intakeItem, equipamentoSugeridoId: equipamentoId, estadiaClienteOrigemId: estadiaId }
        : intakeItem
    );
    atualizarMovimentacao.mutate({ id: movimentacaoId, equipamentosIntake: itensAtualizados });
  };

  return (
    <div className="mt-2 rounded-md border border-indigo-200 bg-indigo-50 p-2">
      <p className="text-xs font-medium uppercase text-indigo-500">Máquina(s) já pronta(s) neste Cliente</p>
      <div className="mt-1 flex flex-wrap gap-2">
        {estadiasOrdenadas.map((estadia) => {
          const equipamento = equipamentoPorId.get(estadia.equipamentoId);
          const cotadaParaEste = estadia.colaboradorCotadoId === colaboradorId && colaboradorId != null;
          return (
            <button
              key={estadia.id}
              type="button"
              onClick={() => handleEscolher(estadia.id, estadia.equipamentoId)}
              disabled={atualizarMovimentacao.isPending}
              className={`rounded-md border px-3 py-1.5 text-sm ${
                cotadaParaEste
                  ? 'border-indigo-400 bg-indigo-100 font-medium text-indigo-800'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {normalizarPatrimonio(equipamento?.patrimonio) ?? 'Sem patrimônio'}
              {equipamento?.modelo ? ` · ${equipamento.modelo}` : ''}
              {cotadaParaEste ? ' (cotada para este colaborador)' : ''}
            </button>
          );
        })}
      </div>
    </div>
  );
}
